'use client';

import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/app/auth-provider';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import type { WebsiteSeoReview } from '@/lib/github/preview/seo';
import { isUsableWebsiteSeoDocument } from '@/lib/github/preview/seo-observation';

const LABELS: Record<string, string> = {
  titles: 'Title',
  descriptions: 'Description',
  canonicals: 'Canonical',
  robots: 'Robots',
  openGraph: 'Open Graph',
  twitter: 'Twitter card',
  alternates: 'Language alternates',
  headings: 'Headings',
  jsonLd: 'JSON-LD',
  content: 'Resource content',
};
const show = (value: unknown) =>
  value === undefined
    ? 'Not observed'
    : typeof value === 'string'
      ? value
      : JSON.stringify(value, null, 2);

export function GitHubPreviewSeoReview({
  previewId,
  revision,
  ready,
}: {
  previewId: string;
  revision: number;
  ready: boolean;
}) {
  const { user } = useAuth();
  const [result, setResult] = useState<WebsiteSeoReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const hasFailedObservations = result?.pages.some(page => !isUsableWebsiteSeoDocument(page.after));
  useEffect(() => {
    setResult(null);
    setError(null);
    if (!ready || !user) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    void (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `/api/github-previews/${encodeURIComponent(previewId)}/seo?revision=${revision}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            cache: 'no-store',
          }
        );
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'SEO inspection is unavailable.');
        if (payload.revision !== revision)
          throw new Error('The preview changed; reopen review for the current revision.');
        if (!controller.signal.aborted) setResult(payload);
      } catch (cause) {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : 'SEO inspection is unavailable.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [previewId, revision, ready, user, attempt]);

  return (
    <Box
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="md"
      bg="ui.surface.card"
      p="3"
    >
      <HStack justify="space-between" mb="2">
        <Text fontSize="xs" fontWeight="750">
          SEO & structured data
        </Text>
        {loading ? <Spinner size="xs" /> : null}
      </HStack>
      {!ready ? (
        <Text fontSize="xs" color="ui.text.secondary">
          SEO review will load after the build finishes.
        </Text>
      ) : null}
      {error ? (
        <HStack align="start">
          <Text fontSize="xs" role="status">
            {error}
          </Text>
          <Button size="2xs" onClick={() => setAttempt(value => value + 1)}>
            Retry
          </Button>
        </HStack>
      ) : null}
      {!error && hasFailedObservations ? (
        <HStack align="start" mb="2">
          <Text fontSize="xs" role="status">
            Some pages could not be inspected. Retry when the preview is ready.
          </Text>
          <Button size="2xs" onClick={() => setAttempt(value => value + 1)}>
            Retry
          </Button>
        </HStack>
      ) : null}
      {result ? (
        <VStack align="stretch" gap="3">
          {result.pages.map(page => (
            <Box key={page.path} as="details">
              <Box as="summary" cursor="pointer" fontSize="xs" fontWeight="650">
                {page.path} ·{' '}
                {!page.before || !isUsableWebsiteSeoDocument(page.after)
                  ? 'Comparison unavailable'
                  : `${page.changedFields.length} fields changed`}
              </Box>
              <VStack align="stretch" gap="2" mt="2">
                {!page.before ? (
                  <Text fontSize="xs">Before-edit baseline unavailable for this route.</Text>
                ) : null}
                {page.after ? (
                  <Text fontSize="2xs" color="ui.text.secondary">
                    HTTP {page.after.status} · inspected{' '}
                    {new Date(page.after.capturedAt).toLocaleString()}
                  </Text>
                ) : null}
                {[
                  ...new Set([
                    ...Object.keys(page.before?.fields || {}),
                    ...Object.keys(page.after?.fields || {}),
                  ]),
                ].map(field => (
                  <Box
                    key={field}
                    as="details"
                    borderTopWidth="1px"
                    borderColor="ui.border.subtle"
                    pt="1"
                  >
                    <Box as="summary" cursor="pointer" fontSize="xs">
                      {LABELS[field] || field}
                      {page.changedFields.includes(field) ? ' · changed' : ''}
                    </Box>
                    <Box overflowX="auto" mt="1">
                      <table style={{ width: '100%', tableLayout: 'fixed', fontSize: 11 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Before</th>
                            <th style={{ textAlign: 'left' }}>After</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {[page.before, page.after].map((document, index) => (
                              <td key={index} style={{ verticalAlign: 'top', padding: 6 }}>
                                <pre
                                  style={{
                                    margin: 0,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    maxHeight: 280,
                                    overflow: 'auto',
                                  }}
                                >
                                  {document ? show(document.fields[field]) : 'Unavailable'}
                                </pre>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                ))}
                {page.after?.issues.map((issue, index) => (
                  <Text
                    key={index}
                    fontSize="xs"
                    color={issue.severity === 'error' ? 'status.danger.fg' : 'ui.text.secondary'}
                  >
                    {issue.message}
                  </Text>
                ))}
                {[
                  ...new Set([
                    ...(page.before?.limitations || []),
                    ...(page.after?.limitations || []),
                  ]),
                ].map(item => (
                  <Text key={item} fontSize="2xs" color="ui.text.tertiary">
                    {item}
                  </Text>
                ))}
              </VStack>
            </Box>
          ))}
          {result.limitations.map(item => (
            <Text key={item} fontSize="2xs" color="ui.text.tertiary">
              {item}
            </Text>
          ))}
        </VStack>
      ) : null}
    </Box>
  );
}
